import { DatePicker } from "@/components/ui/date-picker";
import { useTransactions } from "@/components/history/hooks/useTransactions";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TransactionGrid } from "@/components/returns/TransactionGrid";
import { CreditGrid } from "@/components/returns/CreditGrid";

const Returns = () => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const { data: losses, isLoading: lossesLoading } = useTransactions("loss", "all");
  const { data: returns, isLoading: returnsLoading } = useTransactions("return", "all");
  const { data: credits, isLoading: creditsLoading, refetch: refetchCredits } = useTransactions("credit_sale", "all");
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, string>>({});

  const handlePayment = async (transactionId: string, remainingAmount: number) => {
    const paymentAmount = parseFloat(paymentAmounts[transactionId] || "0");
    
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast.error("Veuillez entrer un montant valide");
      return;
    }

    if (paymentAmount > remainingAmount) {
      toast.error("Le montant ne peut pas dépasser le reste à payer");
      return;
    }

    try {
      const { error: paymentError } = await supabase
        .from('credit_payments')
        .insert({
          transaction_id: transactionId,
          amount: paymentAmount,
          payment_method: "Espèces",
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (paymentError) throw paymentError;

      const newRemainingAmount = remainingAmount - paymentAmount;
      const { error: updateError } = await supabase
        .from('poultry_transactions')
        .update({
          remaining_amount: newRemainingAmount,
          is_fully_paid: newRemainingAmount === 0
        })
        .eq('id', transactionId);

      if (updateError) throw updateError;

      toast.success("Paiement enregistré avec succès");
      setPaymentAmounts(prev => ({ ...prev, [transactionId]: "" }));
      refetchCredits();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error("Erreur lors de l'enregistrement du paiement");
    }
  };

  return (
    <DashboardLayout userType="owner">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Retours, Pertes et Crédits</h1>
          <DatePicker
            selected={selectedDate}
            onSelect={setSelectedDate}
          />
        </div>

        <Tabs defaultValue="returns" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="returns">Retours</TabsTrigger>
            <TabsTrigger value="losses">Pertes</TabsTrigger>
            <TabsTrigger value="credits">Crédits</TabsTrigger>
          </TabsList>
          <TabsContent value="returns" className="mt-4">
            <TransactionGrid
              transactions={returns || []}
              isLoading={returnsLoading}
            />
          </TabsContent>
          <TabsContent value="losses" className="mt-4">
            <TransactionGrid
              transactions={losses || []}
              isLoading={lossesLoading}
            />
          </TabsContent>
          <TabsContent value="credits" className="mt-4">
            <CreditGrid
              transactions={credits || []}
              isLoading={creditsLoading}
              paymentAmounts={paymentAmounts}
              onPaymentAmountChange={(id, value) => 
                setPaymentAmounts(prev => ({ ...prev, [id]: value }))
              }
              onPayment={handlePayment}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Returns;